import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Plus, ShieldCheck, MailWarning, Trash2, Server, Key, AlertCircle, Globe, Cloud, Zap, Send, Box, Edit, RefreshCw, ExternalLink, Inbox } from 'lucide-react';
import api, { API_URL } from '../api/api';
import DataTable from './ui/DataTable';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmContext';

const DEFAULT_FORM = {
  providerType: 'GMAIL',
  user: '',
  pass: '',
  from: '',
  replyTo: '',
  dailyLimit: 100,
  active: true,
  smtpHost: 'smtp.gmail.com',
  smtpPort: 465,
  secure: true,
  imapEnabled: true,
  imapHost: 'imap.gmail.com',
  imapPort: 993,
  imapSecure: true,
  imapUser: '',
  imapPass: ''
};

export default function MailAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general'); // general, smtp, imap

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchAccounts();
    // Handle Microsoft OAuth redirect result
    const params = new URLSearchParams(window.location.search);
    if (params.get('ms_success')) {
      toast.success('Microsoft account connected successfully via OAuth2!');
      window.history.replaceState({}, '', '/mail-accounts');
    } else if (params.get('ms_error')) {
      toast.error(`Microsoft OAuth error: ${params.get('ms_error')}`);
      window.history.replaceState({}, '', '/mail-accounts');
    }
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/mail-accounts');
      setAccounts(res.data?.accounts || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load mail accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    const activeWorkspaceRaw = localStorage.getItem('activeWorkspace');
    let workspaceId = '';
    try {
      const ws = JSON.parse(activeWorkspaceRaw);
      workspaceId = ws?._id || '';
    } catch (_) {}
    const token = localStorage.getItem('token') || '';
    const qs = new URLSearchParams({
      workspaceId,
      token,
      ...(isEditing ? { accountId: editingId } : {})
    });
    window.location.href = `${API_URL}/api/mail-accounts/auth/microsoft?${qs.toString()}`;
  };

  const handleProviderSelect = (provider) => {
    let updates = { providerType: provider };
    if (provider === 'GMAIL') {
      updates = { ...updates, smtpHost: 'smtp.gmail.com', smtpPort: 465, secure: true, imapEnabled: true, imapHost: 'imap.gmail.com', imapPort: 993, imapSecure: true };
    } else if (provider === 'OUTLOOK') {
      updates = { ...updates, smtpHost: 'smtp.office365.com', smtpPort: 587, secure: false, imapEnabled: true, imapHost: 'outlook.office365.com', imapPort: 993, imapSecure: true };
    } else if (provider === 'ZOHO') {
      updates = { ...updates, smtpHost: 'smtppro.zoho.com', smtpPort: 465, secure: true, imapEnabled: true, imapHost: 'imappro.zoho.com', imapPort: 993, imapSecure: true };
    } else if (provider === 'YAHOO') {
      updates = { ...updates, smtpHost: 'smtp.mail.yahoo.com', smtpPort: 465, secure: true, imapEnabled: true, imapHost: 'imap.mail.yahoo.com', imapPort: 993, imapSecure: true };
    } else if (provider === 'SENDGRID') {
      updates = { ...updates, smtpHost: 'smtp.sendgrid.net', smtpPort: 465, secure: true, imapEnabled: false, imapHost: '', imapPort: 993, imapSecure: true };
    } else if (provider === 'MAILGUN') {
      updates = { ...updates, smtpHost: 'smtp.mailgun.org', smtpPort: 465, secure: true, imapEnabled: false, imapHost: '', imapPort: 993, imapSecure: true };
    } else if (provider === 'AWS_SES') {
      updates = { ...updates, smtpHost: 'email-smtp.us-east-1.amazonaws.com', smtpPort: 465, secure: true, imapEnabled: false, imapHost: '', imapPort: 993, imapSecure: true };
    } else {
      updates = { ...updates, smtpHost: '', smtpPort: 465, secure: true, imapEnabled: true, imapHost: '', imapPort: 993, imapSecure: true };
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Sync imapUser/imapPass with user/pass by default if not tracking separately, 
  // but let's let the user type it or default to email.
  const handleEmailChange = (e) => {
    setFormData(prev => ({
      ...prev,
      user: e.target.value,
      imapUser: prev.imapUser === prev.user ? e.target.value : prev.imapUser
    }));
  };

  const handleEdit = (acc) => {
    setFormData({
      providerType: acc.providerType || 'CUSTOM',
      user: acc.user || '',
      pass: '', // Never pre-fill passwords for security
      from: acc.from || '',
      replyTo: acc.replyTo || '',
      dailyLimit: acc.dailyLimit || 100,
      active: acc.active ?? true,
      smtpHost: acc.smtpHost || '',
      smtpPort: acc.smtpPort || 465,
      secure: acc.secure ?? true,
      imapEnabled: acc.imapEnabled ?? false,
      imapHost: acc.imapHost || '',
      imapPort: acc.imapPort || 993,
      imapSecure: acc.imapSecure ?? true,
      imapUser: acc.imapUser || '',
      imapPass: '' // Never pre-fill passwords
    });
    setEditingId(acc._id);
    setIsEditing(true);
    setActiveTab('general');
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (isEditing) {
        await api.put(`/api/mail-accounts/${editingId}`, formData);
        toast.success("Sender account updated successfully.");
      } else {
        await api.post('/api/mail-accounts', formData);
        toast.success("Sender account saved successfully.");
      }
      setIsModalOpen(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData({ ...DEFAULT_FORM });
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save account');
    } finally {
      setFormLoading(false);
    }
  };

  const confirm = useConfirm();

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Account?", "Are you sure you want to completely remove this sender account?");
    if (!ok) return;
    try {
      await api.delete(`/api/mail-accounts/${id}`);
      fetchAccounts();
      toast.success("Account deleted securely.");
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete account');
    }
  };

  const [testingId, setTestingId] = useState(null);

  const handleTestConnection = async (id) => {
    setTestingId(id);
    const promise = new Promise(async (resolve, reject) => {
      try {
        const res = await api.post(`/api/mail-accounts/${id}/test-connection`);
        if (res.data.success) {
          resolve('Connection successful! All protocols are green.');
        } else {
          reject(res.data.lastError || 'Connection failed.');
        }
        fetchAccounts();
      } catch (err) {
        reject(err.response?.data?.error || 'Failed to test connection');
      } finally {
        setTestingId(null);
      }
    });

    toast.promise(promise, {
      loading: 'Testing SMTP/IMAP protocols...',
      success: (data) => data,
      error: (err) => err,
    });
  };

  const columns = [
    {
      header: 'Account Identity',
      accessor: 'user',
      sortable: true,
      render: (acc) => (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl flex items-center justify-center shrink-0 ${acc.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold tracking-tight text-foreground text-sm flex items-center gap-2">
              {acc.from || acc.user}
              <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-muted text-muted-foreground border border-border">
                {acc.providerType || 'SMTP'}
              </span>
            </div>
            <div className="text-[11px] font-semibold text-muted-foreground mt-0.5">{acc.user}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'active',
      sortable: true,
      render: (acc) => (
        <div className="flex flex-col space-y-1.5 mt-1">
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border flex items-center w-fit ${acc.active ? 'bg-success/10 text-success border-success/20' : 'bg-muted/50 text-muted-foreground border-dashed border-border/50'
              }`}>
              {acc.active ? <ShieldCheck className="w-3 h-3 mr-1" /> : <MailWarning className="w-3 h-3 mr-1" />}
              {acc.active ? 'Active' : 'Inactive'}
            </span>
            {acc.healthStatus && acc.healthStatus !== 'GOOD' && (
              <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-tight rounded border flex items-center ${
                acc.healthStatus === 'CRITICAL' ? 'bg-destructive/10 text-destructive border-destructive/20' : 'bg-warning/10 text-warning border-warning/20'
              }`}>
                {acc.healthStatus}
              </span>
            )}
          </div>
          {acc.lastError && (
             <div className="text-[10px] font-medium text-destructive truncate max-w-[200px]" title={acc.lastError}>
               Error: {acc.lastError}
             </div>
          )}
          {acc.cooldownUntil && new Date(acc.cooldownUntil) > new Date() && (
             <div className="text-[10px] font-bold text-warning flex items-center">
               <Zap className="w-3 h-3 mr-1" /> Cooling down until {new Date(acc.cooldownUntil).toLocaleTimeString()}
             </div>
          )}
          {acc.imapEnabled && (
            <span className="text-[10px] font-bold text-muted-foreground flex items-center">
              <Server className="w-3 h-3 mr-1" /> IMAP Synced
            </span>
          )}
        </div>
      )
    },
    {
      header: 'Sending Limits',
      accessor: 'dailyLimit',
      sortable: false,
      render: (acc) => (
        <div className="flex flex-col">
          <div className="text-sm font-bold text-foreground">{acc.sentToday || 0} / {acc.dailyLimit}</div>
          <div className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Sent Today</div>
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: '_id',
      sortable: false,
      align: 'right',
      render: (acc) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/inbox?mailAccountId=${acc._id}`)}
            className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors border border-transparent hover:border-primary/20 shadow-sm"
            title="Gelen Kutusunu Aç (View Inbox)"
          >
            <Inbox className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleTestConnection(acc._id)}
            disabled={testingId === acc._id}
            className={`p-2 rounded-lg transition-colors border border-transparent shadow-sm ${
              acc.healthStatus === 'CRITICAL' 
                ? 'text-warning hover:bg-warning/10 hover:border-warning/20 animate-pulse' 
                : 'text-success hover:bg-success/10 hover:border-success/20'
            }`}
            title="Reconnect & Test"
          >
            <RefreshCw className={`w-4 h-4 ${testingId === acc._id ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => handleEdit(acc)}
            className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors border border-transparent hover:border-primary/20 shadow-sm"
            title="Edit Account"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(acc._id)}
            className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors border border-transparent hover:border-destructive/20 shadow-sm"
            title="Delete Account"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const renderHelperText = () => {
    switch (formData.providerType) {
      case 'GMAIL':
        return (
          <Alert className="bg-primary/5 text-primary border-primary/20 mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Google Workspace & Gmail</AlertTitle>
            <AlertDescription className="text-xs font-medium">
              Google requires an **App Password** for SMTP/IMAP connections instead of your normal password. Ensure 2-Step Verification is enabled, then generate an App Password in your Google Account Security settings.
            </AlertDescription>
          </Alert>
        );
      case 'OUTLOOK':
        return (
          <Alert className="bg-primary/5 text-primary border-primary/20 mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Microsoft 365 & Outlook</AlertTitle>
            <AlertDescription className="text-xs font-medium">
              Ensure Authenticated SMTP is enabled for your Microsoft 365 tenant. Basic Auth may need to be permitted, or you must use an App Password if Multi-Factor Authentication is enforced.
            </AlertDescription>
          </Alert>
        );
      case 'ZOHO':
      case 'YAHOO':
        return (
          <Alert className="bg-primary/5 text-primary border-primary/20 mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>{formData.providerType === 'ZOHO' ? 'Zoho Mail' : 'Yahoo Mail'}</AlertTitle>
            <AlertDescription className="text-xs font-medium">
              Ensure you generate an App Password for secure SMTP/IMAP access. Don't use your primary account password.
            </AlertDescription>
          </Alert>
        );
      case 'SENDGRID':
      case 'MAILGUN':
      case 'AWS_SES':
        return (
          <Alert className="bg-primary/5 text-primary border-primary/20 mb-6">
            <Server className="w-4 h-4" />
            <AlertTitle>Transactional Provider</AlertTitle>
            <AlertDescription className="text-xs font-medium">
              Provide your API Key or SMTP credentials. Transactional providers generally do not support IMAP directly for inbound parsing via the same host credentials.
            </AlertDescription>
          </Alert>
        );
      default:
        return (
          <Alert className="bg-muted text-muted-foreground mb-6 border-border">
            <Globe className="w-4 h-4" />
            <AlertTitle>Custom SMTP/IMAP</AlertTitle>
            <AlertDescription className="text-xs font-medium">
              Manually configure your Host, Port, and TLS parameters for your specific email infrastructure (e.g. private servers).
            </AlertDescription>
          </Alert>
        );
    }
  }

  return (
    <PageShell className="animate-in fade-in duration-500">
      <PageHeader
        title="Sender Accounts"
        subtitle="Configure deep SMTP/IMAP integrations for outbound outreach and inbox parsing."
        icon={Mail}
        actions={
          <button
            onClick={() => { setFormData({ ...DEFAULT_FORM }); setIsEditing(false); setEditingId(null); setActiveTab('general'); setIsModalOpen(true); }}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-xl shadow-sm text-sm font-bold hover:bg-primary/90 flex items-center transition-all"
          >
            <Plus className="w-5 h-5 mr-2" />
            Connect Sender
          </button>
        }
      />

      {error && <div className="bg-destructive/10 text-destructive text-sm font-medium p-4 rounded-xl border border-destructive/20">{error}</div>}

      <DataTable
        columns={columns}
        data={accounts}
        loading={loading}
        pagination={{ page: 1, limit: 100, totalRecords: accounts.length, totalPages: 1 }}
        emptyState={
          <EmptyState
            title="No Sender Accounts"
            description="You need to connect an SMTP identity to execute campaigns."
            icon={Mail}
          />
        }
      />

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in zoom-in-95">
          <form onSubmit={handleSave} className="bg-card rounded-[2.5rem] shadow-2xl w-full max-w-3xl overflow-hidden border border-border/40 flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-border bg-card/60 backdrop-blur-xl flex justify-between items-center shrink-0">
              <div>
                <h2 className="text-2xl font-black tracking-tight text-foreground flex items-center gap-3">
                  {isEditing ? 'Update Mailbox' : 'Configure Mailbox'}
                </h2>
                <p className="text-xs font-semibold text-muted-foreground mt-1">
                  {isEditing ? 'Synchronize and update active mailbox parameters.' : 'Setup outbound dispatch protocols and inbound IMAP parsing.'}
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
              {/* Sidebar Navigation */}
              <div className="w-full md:w-56 bg-muted/20 border-r border-border p-4 flex flex-row md:flex-col gap-2 shrink-0 overflow-x-auto">
                <button type="button" onClick={() => setActiveTab('general')} className={`flex items-center gap-2.5 px-4 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'general' ? 'bg-primary text-primary-foreground shadow-md' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                  <Mail className="w-4 h-4" /> Identity & Limits
                </button>
                <button type="button" onClick={() => setActiveTab('smtp')} className={`flex items-center gap-2.5 px-4 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'smtp' ? 'bg-primary text-primary-foreground shadow-md' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                  <Server className="w-4 h-4" /> SMTP Routing
                </button>
                <button type="button" onClick={() => setActiveTab('imap')} className={`flex items-center gap-2.5 px-4 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'imap' ? 'bg-primary text-primary-foreground shadow-md' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
                  <Key className="w-4 h-4" /> IMAP Parsing
                </button>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-card">
                {renderHelperText()}

                {activeTab === 'general' && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                    {/* Provider Presets */}
                    <div>
                      <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-3 opacity-60">Provider Architecture</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button type="button" onClick={() => handleProviderSelect('GMAIL')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'GMAIL' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-sm mb-2"><svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" /><path fill="#34A853" d="M16.909 21h3.818c.904 0 1.636-.732 1.636-1.636V11.73l-5.454 4.091v5.18z" /><path fill="#FBBC05" d="M24 5.457v6.273l-5.455 4.091L12 16.64l-6.545-4.91L0 11.73V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.548l8.073-6.055C21.69 2.28 24 3.434 24 5.457z" /><path fill="#4285F4" d="M0 11.73v7.636C0 20.27.732 21 1.636 21h3.819v-5.18L0 11.73z" /></svg></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Gmail</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('OUTLOOK')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'OUTLOOK' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0078D4] shadow-sm mb-2"><svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Microsoft</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('ZOHO')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'ZOHO' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-500 shadow-sm mb-2"><Box className="w-4 h-4 text-white" /></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Zoho Mail</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('YAHOO')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'YAHOO' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#6001D2] shadow-sm mb-2"><span className="text-white font-black italic text-sm">Y!</span></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Yahoo</div>
                        </button>

                        <button type="button" onClick={() => handleProviderSelect('SENDGRID')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'SENDGRID' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0263E0] shadow-sm mb-2"><Send className="w-4 h-4 text-white" /></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">SendGrid</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('MAILGUN')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'MAILGUN' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DB2A2A] shadow-sm mb-2"><Zap className="w-4 h-4 text-white" /></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Mailgun</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('AWS_SES')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'AWS_SES' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FF9900] shadow-sm mb-2"><Cloud className="w-4 h-4 text-white" /></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">AWS SES</div>
                        </button>
                        <button type="button" onClick={() => handleProviderSelect('CUSTOM')} className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all ${formData.providerType === 'CUSTOM' ? 'border-primary bg-primary/10 shadow-sm transform scale-[1.02]' : 'border-border bg-muted/30 hover:border-primary/40'}`}>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-muted-foreground shadow-sm mb-2"><Globe className="w-4 h-4 text-white" /></div>
                          <div className="font-bold text-xs tracking-tight text-foreground">Custom</div>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Authentication Email Address</label>
                        <input
                          type="email"
                          required
                          value={formData.user}
                          onChange={handleEmailChange}
                          placeholder="e.g. founder@acme.com"
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">From Name</label>
                        <input
                          type="text"
                          required
                          value={formData.from}
                          onChange={e => setFormData({ ...formData, from: e.target.value })}
                          placeholder="e.g. Jane | Acme"
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Reply-To Address (Optional)</label>
                        <input
                          type="email"
                          value={formData.replyTo}
                          onChange={e => setFormData({ ...formData, replyTo: e.target.value })}
                          placeholder="e.g. support@acme.com"
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 pt-4 border-t border-border">
                      <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Daily Velocity Limit</label>
                        <input
                          type="number"
                          min="1"
                          required
                          value={formData.dailyLimit}
                          onChange={e => setFormData({ ...formData, dailyLimit: parseInt(e.target.value) })}
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-5 transition-all"
                        />
                        <p className="text-[10px] font-medium text-muted-foreground mt-2">Maximum emails sent per 24 hours.</p>
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center gap-3 cursor-pointer mt-4 p-4 rounded-2xl border-2 border-border w-full hover:border-primary/50 transition-colors">
                          <input
                            type="checkbox"
                            checked={formData.active}
                            onChange={e => setFormData({ ...formData, active: e.target.checked })}
                            className="w-5 h-5 rounded-md border-2 border-muted-foreground/30 focus:ring-primary text-primary"
                          />
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-foreground">Pool Active</span>
                            <span className="text-[10px] font-medium text-muted-foreground">Enable for campaign dispatch</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'smtp' && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 text-foreground">
                    <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-6">
                      <h3 className="font-black text-sm text-primary mb-1">Outbound Delivery (SMTP)</h3>
                      <p className="text-xs text-muted-foreground font-medium">This module ensures payloads reach recipient servers reliably. Your master password is encrypted via VaultEngine hardware simulation.</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">SMTP Host</label>
                        <input
                          type="text"
                          required
                          value={formData.smtpHost}
                          onChange={e => setFormData({ ...formData, smtpHost: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Port</label>
                        <input
                          type="number"
                          required
                          value={formData.smtpPort}
                          onChange={e => setFormData({ ...formData, smtpPort: parseInt(e.target.value) })}
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                        />
                      </div>
                    </div>

                    {formData.providerType === 'OUTLOOK' ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-5">
                        <div className="flex flex-col items-center gap-3 text-center">
                          <div className="w-14 h-14 rounded-2xl bg-[#0078D4]/10 border-2 border-[#0078D4]/20 flex items-center justify-center">
                            <svg className="w-7 h-7" viewBox="0 0 23 23">
                              <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                              <path fill="#f35325" d="M1 1h10v10H1z"/>
                              <path fill="#81bc06" d="M12 1h10v10H12z"/>
                              <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                              <path fill="#ffba08" d="M12 12h10v10H12z"/>
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-black text-foreground text-base">Microsoft OAuth2</h3>
                            <p className="text-xs text-muted-foreground font-medium mt-1 max-w-xs">
                              Securely connect your Microsoft / Outlook account. No password stored — tokens are refreshed automatically.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleMicrosoftLogin}
                          className="flex items-center gap-3 px-6 py-3.5 bg-[#0078D4] hover:bg-[#106EBE] text-white font-bold rounded-2xl transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 23 23">
                            <path fill="white" d="M0 0h10.5v10.5H0zM12.5 0H23v10.5H12.5zM0 12.5h10.5V23H0zM12.5 12.5H23V23H12.5z"/>
                          </svg>
                          {isEditing ? 'Re-authenticate with Microsoft' : 'Sign in with Microsoft'}
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        {isEditing && (
                          <p className="text-[10px] text-muted-foreground font-medium">
                            Re-authenticate to refresh OAuth2 tokens if they have expired.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">SMTP Password (App Password)</label>
                        <input
                          type="password"
                          required={!isEditing}
                          value={formData.pass}
                          onChange={e => setFormData({ ...formData, pass: e.target.value })}
                          placeholder={isEditing ? '•••••••• (Leave blank to keep current)' : '••••••••••••••••'}
                          className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all font-mono"
                        />
                      </div>
                    )}

                    <label className="flex justify-between items-center cursor-pointer p-4 rounded-2xl border-2 border-border w-full hover:border-primary/50 transition-colors">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-foreground">Require TLS/SSL (Secure)</span>
                        <span className="text-[10px] font-medium text-muted-foreground">Force encryption layer for transport.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.secure}
                        onChange={e => setFormData({ ...formData, secure: e.target.checked })}
                        className="w-5 h-5 rounded-md border-2 border-muted-foreground/30 focus:ring-primary text-primary"
                      />
                    </label>
                  </div>
                )}

                {activeTab === 'imap' && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300 text-foreground">
                    <label className={`flex justify-between items-center cursor-pointer p-4 rounded-2xl border-2 transition-colors duration-300 ${formData.imapEnabled ? 'bg-primary/5 border-primary shadow-sm' : 'border-border hover:border-primary/50'}`}>
                      <div className="flex flex-col">
                        <span className={`text-sm font-bold ${formData.imapEnabled ? 'text-primary' : 'text-foreground'}`}>Enable Inbound Parsing (IMAP)</span>
                        <span className="text-[10px] font-medium text-muted-foreground">Track prospect replies, bounces, and thread history.</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.imapEnabled}
                        onChange={e => setFormData({ ...formData, imapEnabled: e.target.checked })}
                        className="w-5 h-5 rounded-md border-2 border-muted-foreground/30 focus:ring-primary text-primary"
                      />
                    </label>

                    <div className={`transition-all duration-500 overflow-hidden ${formData.imapEnabled ? 'opacity-100 max-h-[800px]' : 'opacity-40 max-h-0 pointer-events-none'}`}>
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="col-span-2">
                          <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">IMAP Host</label>
                          <input
                            type="text"
                            value={formData.imapHost}
                            onChange={e => setFormData({ ...formData, imapHost: e.target.value })}
                            placeholder="imap.gmail.com"
                            className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Port</label>
                          <input
                            type="number"
                            value={formData.imapPort}
                            onChange={e => setFormData({ ...formData, imapPort: parseInt(e.target.value) })}
                            className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                          <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">IMAP Username</label>
                          <input
                            type="email"
                            value={formData.imapUser}
                            onChange={e => setFormData({ ...formData, imapUser: e.target.value })}
                            placeholder="Often same as SMTP email"
                            className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">IMAP Password</label>
                          <input
                            type="password"
                            value={formData.imapPass}
                            onChange={e => setFormData({ ...formData, imapPass: e.target.value })}
                            placeholder={isEditing ? '•••••••• (Leave blank to keep current)' : '••••••••••••••••'}
                            className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all font-mono"
                          />
                        </div>
                      </div>

                      <label className="flex justify-between items-center cursor-pointer p-4 rounded-2xl border-2 border-border w-full hover:border-primary/50 transition-colors">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-foreground">Require TLS/SSL (Secure)</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.imapSecure}
                          onChange={e => setFormData({ ...formData, imapSecure: e.target.checked })}
                          className="w-5 h-5 rounded-md border-2 border-muted-foreground/30 focus:ring-primary text-primary"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-5 border-t border-border bg-card/60 backdrop-blur-xl flex justify-between items-center shrink-0">
              <div>
                {activeTab !== 'general' && (
                  <button type="button" onClick={() => setActiveTab('general')} className="px-4 py-2.5 text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl transition-all">
                    &larr; Back to Limits
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-xs font-black uppercase tracking-widest text-muted-foreground transition-all hover:text-foreground">Cancel</button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-8 py-3.5 text-xs font-black uppercase tracking-widest text-white bg-primary rounded-xl shadow-lg transition-all active:scale-[0.98] hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {formLoading ? <div className="animate-spin w-4 h-4 border-2 border-white/50 border-t-white rounded-full" /> : <ShieldCheck className="w-4 h-4" />}
                  {formLoading ? 'Applying Sec-Profiles...' : (isEditing ? 'Update Authorization' : 'Authorize Vault Storage')}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}
